/**
 * The library — blueprints and themes, and the copy-not-link contract stated
 * in the interface rather than in a doc.
 *
 * Visual Reference: `design/screens/library.html` — default (Blueprints) ·
 * themes · loading · empty · error.
 *
 * "USED BY N" IS NOT A STORED NUMBER. `docs/api-contract.md` says "computed,
 * not stored", and the server counts it from `funnels.created_from_preset_id`
 * on every read. A cached count is a number that goes wrong silently, and being
 * told a figure that is not true is worse than not being told one.
 *
 * KNOWLEDGE BASES IS DISABLED FOR EVERYONE. DECISION-016 and DECISION-063: M5
 * work, drawn where the export draws it and disabled — not gated by plan, which
 * would tell a Free operator to upgrade for something nobody can have yet.
 *
 * Story 7.4.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiFailure, apiGet, apiPost } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Skeleton } from '../ui/Skeleton'

/* ── the export's own copy, verbatim ────────────────────────────────────── */

const LEAD_IN =
  'Start a funnel from something that already works, or reuse a look across every funnel you run.'

/**
 * PRD §3.3's contract, in the interface. It is rendered under the grid rather
 * than in a doc because the thing it promises — that improving a blueprint
 * never touches a running funnel — is the thing an operator would otherwise
 * have to find out by being surprised.
 */
const SNAPSHOT_CONTRACT =
  'A blueprint is a snapshot. Using one copies its steps, rubric and bands into a new funnel — ' +
  'improving the blueprint later never changes a funnel that is already running.'

const EMPTY_BLUEPRINTS = {
  title: 'Your three starting blueprints are here',
  body: "You haven't saved one of your own yet. Build a funnel, then save it as a blueprint from the funnels list.",
}
const EMPTY_THEMES = {
  title: 'Your three starting themes are here',
  body: "You haven't saved one of your own yet. Apply any of these to a funnel, adjust it in the Design tab, and it appears here.",
}

type Kind = 'blueprint' | 'theme'

type PresetCard = {
  id: string
  kind: Kind
  name: string
  description: string | null
  version: number
  archetype: string | null
  curated: boolean
  savedByName: string | null
  usedBy: number
}

/** PRD §3.1's five archetypes, in the operator's words — the funnels list's map. */
const ARCHETYPE: Record<string, string> = {
  scorecard: 'Scorecard',
  qualifier: 'Qualifier',
  recommender: 'Recommender',
  triage: 'Triage',
  eligibility: 'Eligibility',
}

export function Library() {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [kind, setKind] = useState<Kind>('blueprint')
  const [presets, setPresets] = useState<PresetCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setPresets(null)
    setError(null)
    try {
      const view = await apiGet<{ presets: PresetCard[] }>(
        `/v1/w/${workspaceId}/presets?kind=${kind}`,
      )
      setPresets(view.presets)
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't load your library. Check your connection and try again.",
      )
    }
  }, [workspaceId, kind])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * "Use this blueprint" is `POST /v1/w/{wid}/funnels` with a preset id —
   * `docs/api-contract.md` puts "from blank or from a preset" on one route, so
   * the funnel cap and the archetype check apply to both. The server does the
   * copying; nothing here reads a preset body.
   */
  const use = async (preset: PresetCard) => {
    if (!workspaceId || busy) return
    setBusy(preset.id)
    setError(null)
    try {
      const created = await apiPost<{ id: string }>(`/v1/w/${workspaceId}/funnels`, {
        name: preset.name,
        preset_id: preset.id,
      })
      window.location.assign(`/funnels/${created.id}`)
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't start a funnel from that. Check your connection and try again.",
      )
      setBusy(null)
    }
  }

  const empty = kind === 'blueprint' ? EMPTY_BLUEPRINTS : EMPTY_THEMES

  return (
    <div className="flex flex-col gap-mdx-20">
      <div className="flex flex-col gap-mdx-6">
        <h1 className="m-0 text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
          Library
        </h1>
        <p className="m-0 max-w-mdx-measure-60 text-mdx-15 text-mdx-slate">{LEAD_IN}</p>
      </div>

      <div className="overflow-x-auto border-b border-mdx-line">
        <div className="flex min-w-max items-center gap-mdx-28">
          <Tab label="Blueprints" active={kind === 'blueprint'} onSelect={() => setKind('blueprint')} />
          <Tab label="Themes" active={kind === 'theme'} onSelect={() => setKind('theme')} />
          {/*
            M5, and disabled for EVERYONE rather than gated by plan —
            DECISION-016. A plan gate here would sell an upgrade for something
            that does not exist yet, which is the defect DECISION-016 names.
            Drawn where the export draws it: DECISION-063's house rule.
          */}
          <span
            title="Used by the voice agent."
            aria-disabled="true"
            className="inline-flex cursor-not-allowed items-center gap-mdx-8 whitespace-nowrap border-b-2 border-transparent px-mdx-4 py-mdx-12 text-mdx-15 font-mdx-medium text-mdx-slate opacity-mdx-recessed"
          >
            <span
              aria-hidden="true"
              className="inline-block size-mdx-14 flex-none rounded-mdx-3 border border-solid border-mdx-slate"
            />
            Knowledge bases
            <span className="rounded-mdx-pill bg-mdx-nurture-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-slate">
              Coming soon
            </span>
          </span>
        </div>
      </div>

      {error ? <Callout>{error}</Callout> : null}

      {!presets ? (
        <div className="grid grid-cols-1 gap-mdx-16 md:grid-cols-2 xl:grid-cols-3">
          {/*
            The real card with blocks in its slots — `DESIGN-SYSTEM.md` §2:
            "preserve the real layout so nothing jumps when content lands." It
            also means the card's height stays a consequence of its padding and
            type rather than a number this file would have to invent a token for.
          */}
          {[0, 1, 2].map((nth) => (
            <article
              key={nth}
              className="flex flex-col gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card"
            >
              <Skeleton className="h-mdx-24" style={{ width: '46%' }} />
              <Skeleton className="h-mdx-20" style={{ width: '72%' }} />
              <Skeleton className="h-mdx-18" style={{ width: '90%' }} />
              <Skeleton className="h-mdx-18" style={{ width: '38%' }} />
            </article>
          ))}
        </div>
      ) : presets.length === 0 ? (
        <div className="flex flex-col gap-mdx-6 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{empty.title}</span>
          <span className="max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">{empty.body}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-mdx-16 md:grid-cols-2 xl:grid-cols-3">
          {presets.map((preset) => (
            <Card
              key={preset.id}
              preset={preset}
              busy={busy === preset.id}
              onUse={() => void use(preset)}
            />
          ))}
        </div>
      )}

      {kind === 'blueprint' ? (
        <p className="m-0 max-w-mdx-measure-60 text-mdx-13 text-mdx-slate">{SNAPSHOT_CONTRACT}</p>
      ) : null}
    </div>
  )
}

function Tab({
  label,
  active,
  onSelect,
}: {
  label: string
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
      className={[
        'cursor-pointer whitespace-nowrap border-0 border-b-2 bg-transparent px-mdx-4 py-mdx-12 text-mdx-15',
        active
          ? 'border-mdx-brand font-mdx-semibold text-mdx-ink'
          : 'border-transparent font-mdx-medium text-mdx-slate',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

function Card({
  preset,
  busy,
  onUse,
}: {
  preset: PresetCard
  busy: boolean
  onUse: () => void
}) {
  const noun = preset.kind === 'blueprint' ? 'funnel' : 'funnel'
  const usage =
    preset.kind === 'blueprint'
      ? `Used by ${preset.usedBy} ${preset.usedBy === 1 ? noun : `${noun}s`}`
      : `On ${preset.usedBy} ${preset.usedBy === 1 ? noun : `${noun}s`}`

  return (
    <article className="flex flex-col gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-wrap items-center gap-mdx-8">
        {preset.archetype ? (
          <Chip>{ARCHETYPE[preset.archetype] ?? preset.archetype}</Chip>
        ) : null}
        {/*
          THE VERSION CHIP IS ON EVERY CARD, and that is the point rather than
          decoration: a blueprint is a snapshot, so which snapshot you took
          matters, and the funnel records it (PRD §3.3).
        */}
        <Chip tabular>v{preset.version}</Chip>
        {preset.curated ? (
          <span className="ml-auto inline-flex items-center rounded-mdx-pill bg-mdx-brand-tint px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold text-mdx-brand-deep">
            Recommended
          </span>
        ) : preset.savedByName ? (
          <Chip>Saved by {preset.savedByName}</Chip>
        ) : null}
      </div>

      <div className="flex flex-col gap-mdx-6">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{preset.name}</span>
        {preset.description ? (
          <span className="text-mdx-15 text-mdx-slate">{preset.description}</span>
        ) : null}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-mdx-12">
        <span className="text-mdx-13 text-mdx-slate tabular-nums">{usage}</span>
        {preset.kind === 'blueprint' ? (
          <span className="ml-auto">
            <Button variant="ghost" size="small" onClick={onUse} disabled={busy}>
              {busy ? 'Starting…' : 'Use this blueprint'}
            </Button>
          </span>
        ) : (
          /*
            A THEME CARD HAS NO ACTION — DECISION-121 (Q-086), Story 16.2.

            `Apply to a funnel` stood here drawn and disabled under
            DECISION-063's house rule. What removed it is that it was a shortcut
            to a capability that is already reachable, not a missing one:
            `DesignTab.tsx` renders the same three themes from `editor/themes.ts`
            and writes `theme_ref = { kind: 'preset', preset_id }` on select —
            PRD §3.3's "a theme can be applied to an existing funnel without
            touching content", which `e2e/editor-design.spec.ts` holds to. No
            custom theme can be authored at MVP (`M6_DEFERRAL`), so the two sets
            are the same set and this screen's version reached nothing extra.

            The card says where the action lives instead of offering a dead one,
            and it is NOT pushed right: the mockup sets `margin-left: auto` on
            the blueprint button and not on this line. `library.html`,
            re-ingested 2026-09-08 — the mockup lost the control first.
          */
          <span className="text-mdx-13 text-mdx-slate">Applied in a funnel’s Design tab</span>
        )}
      </div>
    </article>
  )
}

function Chip({ children, tabular = false }: { children: React.ReactNode; tabular?: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-mdx-pill bg-mdx-nurture-tint px-mdx-12 py-mdx-5',
        'text-mdx-13 font-mdx-semibold text-mdx-slate',
        tabular ? 'tabular-nums' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  )
}
